/**
 * Dove atterra un ruolo, e quali sezioni tiene in cima alla barra.
 *
 * Sostituisce due elenchi di ruoli scritti a mano nel codice (`nav-config.ts` e
 * `DashboardLanding.tsx`), che dovevano concordare e concordavano solo perché
 * qualcuno se lo ricordava.
 *
 * ⚠️ **Il pannello avvisa quando la destinazione non è visibile a quel ruolo.**
 * È il difetto che questa piattaforma ha già incontrato tre volte sotto il nome
 * di «403 che non si vede»: chi configura è staff e scavalca ogni cancello,
 * quindi una destinazione vietata sembra funzionare finché non la prova la
 * persona vera — che ci atterra sopra appena fa login.
 */
import { AlertTriangle, Check, Loader2, MapPin } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  destinazioniAtterraggio,
  moduliPromuovibili,
} from "@/lib/nav-config";
import { hasPermesso, hasPermessoSezione } from "@/lib/permessi";
import { cn } from "@/lib/utils";
import type { Atterraggio, PermessiUtente, PermessoRiga, Ruolo } from "@/types/api";

const DESTINAZIONI = destinazioniAtterraggio();
const MODULI = moduliPromuovibili();

/** I permessi del ruolo, nella forma che `hasPermesso` sa leggere. Serve a
 *  rispondere a una domanda sola: *questo ruolo ci arriverebbe?* */
function permessiDelRuolo(ruolo: Ruolo, righe: PermessoRiga[]): PermessiUtente {
  const espliciti: Record<string, Record<string, boolean>> = {};
  for (const r of righe) {
    (espliciti[r.risorsa_tipo] ??= {})[r.risorsa_key] = r.abilitato;
  }
  return {
    ruolo,
    // ⚠️ `false` di proposito: qui si guarda **il ruolo**, non chi sta
    // configurando. Mettendo `true` (come è vero per lo staff che legge questa
    // pagina) ogni destinazione risulterebbe raggiungibile e l'avviso non
    // scatterebbe mai — che è esattamente il modo in cui i 403 invisibili sono
    // arrivati in produzione.
    is_maitime_staff: false,
    ruolo_configurato: righe.length > 0,
    permessi_espliciti: espliciti as PermessiUtente["permessi_espliciti"],
    atterraggio: { path: "/", sezioni_in_cima: null },
  };
}

function raggiungibile(perms: PermessiUtente, path: string): boolean {
  const d = DESTINAZIONI.find((x) => x.path === path);
  if (!d) return true; // rotta fuori catalogo: non ci si può pronunciare
  if (d.moduloKey) return hasPermessoSezione(perms, d.permessoKey!, d.moduloKey);
  if (!d.permessoTipo) return true;
  return hasPermesso(perms, d.permessoTipo, d.permessoKey ?? "");
}

export function AtterraggioRuolo({
  ruolo,
  valore,
  righe,
  onSalva,
  salvando,
  salvato,
}: {
  ruolo: Ruolo;
  valore: Atterraggio;
  /** Le righe di permesso attualmente mostrate nella matrice: l'avviso si
   *  calcola su **quelle**, non su quelle salvate, così cambiando un modulo si
   *  vede subito l'effetto sull'atterraggio. */
  righe: PermessoRiga[];
  onSalva: (a: Atterraggio) => void;
  salvando: boolean;
  salvato: boolean;
}) {
  const [path, setPath] = useState(valore.path);
  const [sezioni, setSezioni] = useState(valore.sezioni_in_cima ?? "");
  useEffect(() => {
    setPath(valore.path);
    setSezioni(valore.sezioni_in_cima ?? "");
  }, [valore.path, valore.sezioni_in_cima]);

  const sporco = path !== valore.path || (sezioni || null) !== valore.sezioni_in_cima;
  const perms = permessiDelRuolo(ruolo, righe);
  const destinazioneOk = raggiungibile(perms, path);
  const moduloOk =
    !sezioni || hasPermesso(perms, "modulo", sezioni);

  return (
    <section className="rounded-lg border bg-card p-4">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        <MapPin className="size-4 text-brand-700" />
        Atterraggio
      </h3>
      <p className="mt-0.5 max-w-2xl text-[12px] text-muted-foreground">
        Dove porta <code className="rounded bg-muted px-1">/</code> per questo ruolo, e quale
        modulo tiene le proprie sezioni <b>in cima alla barra</b> invece che annidate. La barra
        non aggiunge nessuna voce di atterraggio: se la destinazione è una sezione, la si
        raggiunge dentro il suo modulo.
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="mb-1 block text-[11px] text-muted-foreground">
            Atterra su
          </span>
          <select
            value={path}
            onChange={(e) => setPath(e.target.value)}
            className={cn(
              "h-9 min-w-[240px] rounded-md border bg-card px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring",
              !destinazioneOk && "border-amber-500",
            )}
          >
            {DESTINAZIONI.map((d) => (
              <option key={d.path} value={d.path}>
                {d.label}
              </option>
            ))}
            {/* Una rotta salvata che non è più in catalogo non deve sparire dal
                menù: altrimenti il primo salvataggio la cambierebbe di nascosto. */}
            {!DESTINAZIONI.some((d) => d.path === path) && (
              <option value={path}>{path} (rotta non in catalogo)</option>
            )}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-[11px] text-muted-foreground">
            Sezioni in cima
          </span>
          <select
            value={sezioni}
            onChange={(e) => setSezioni(e.target.value)}
            className={cn(
              "h-9 min-w-[180px] rounded-md border bg-card px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring",
              !moduloOk && "border-amber-500",
            )}
          >
            <option value="">nessuna (moduli annidati)</option>
            {MODULI.map((m) => (
              <option key={m.codice} value={m.codice}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-2">
          <Button size="sm" disabled={!sporco || salvando} onClick={() => onSalva({ path, sezioni_in_cima: sezioni || null })}>
            {salvando ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
            Salva atterraggio
          </Button>
          {salvato && !sporco && (
            <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700 dark:text-emerald-400">
              <Check className="size-3.5" /> salvato
            </span>
          )}
        </div>
      </div>

      {(!destinazioneOk || !moduloOk) && (
        <p className="mt-3 flex items-start gap-1.5 rounded border border-amber-400 bg-amber-50 px-2.5 py-2 text-[11.5px] dark:border-amber-900 dark:bg-amber-950">
          <AlertTriangle className="mt-px size-3.5 shrink-0" />
          <span>
            {!destinazioneOk && (
              <>
                Con i permessi qui sopra questo ruolo <b>non vede</b> la destinazione scelta:
                farebbe login e atterrerebbe su una pagina vietata.{" "}
              </>
            )}
            {!moduloOk && (
              <>
                E non ha il modulo <b>{sezioni}</b>, quindi non ci sarebbe nessuna sezione da
                mettere in cima.
              </>
            )}
          </span>
        </p>
      )}
    </section>
  );
}
